/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  KeyboardAvoidingView,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { showInfo } from '../../shared/ui/appToast';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { OperationalFilterChipGroup } from '../../shared/filters/OperationalFilterPrimitives';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { CatalogCategorySelectorModal } from '../../catalog/public-api';
import { CatalogStockProductSearchFields } from '../../shared/product-search/components/CatalogStockProductSearchFields';
import { searchProducts } from '../../catalog/infra-api';
import { ModalFormSection } from '../../shared/modals/ModalFormSection'
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { PromotionVariantPickerModal } from './PromotionVariantPickerModal';
import {
  PROMOTION_APPLIES_TO_HELPER,
  PROMOTION_APPLY_MODE_HELPERS,
  PROMOTION_APPLY_MODE_LABELS,
  PROMOTION_KIND_HINTS,
  PROMOTION_KIND_LABELS,
  PROMOTION_SCOPE_LABELS,
  PROMOTION_TARGET_TYPE_LABELS,
  formatPromotionDraftBenefit,
  getPromotionScopeHelperText,
} from '../application/promotionPresentation';
import { usePromotionEditScreen } from '../application/usePromotionEditScreen';
import type { PromotionConflict } from '../application/promotionConflicts';
import type { PromotionApplyMode, PromotionChannel, PromotionKind, PromotionScope, PromotionWithTargets, Weekday } from '../domain/promotionTypes';
import { ticketColors } from '../../shared/theme/ticketColors';

const KIND_OPTIONS = (Object.keys(PROMOTION_KIND_LABELS) as PromotionKind[])
  .filter((kind) => kind !== 'manual')
  .map((kind) => ({
    value: kind,
    label: PROMOTION_KIND_LABELS[kind],
    hint: PROMOTION_KIND_HINTS[kind] ?? '',
  }));

/** Scope `category` is a legacy alias — only expose line/cart in UI. */
const SCOPE_OPTIONS: Array<{ value: PromotionScope; label: string }> = [
  { value: 'line', label: PROMOTION_SCOPE_LABELS.line },
  { value: 'cart', label: PROMOTION_SCOPE_LABELS.cart },
];

const APPLY_MODE_OPTIONS = (Object.keys(PROMOTION_APPLY_MODE_LABELS) as PromotionApplyMode[]).map(
  (mode) => ({
    value: mode,
    label: PROMOTION_APPLY_MODE_LABELS[mode],
    helper: PROMOTION_APPLY_MODE_HELPERS[mode],
  })
);

const WEEKDAY_OPTIONS: Array<{ value: Weekday; label: string }> = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];

type Props = {
  visible: boolean;
  promotionId: string | null;
  duplicateFromId?: string | null;
  createTemplateId?: string | null;
  onClose: () => void;
  /**
   * Detector de pisadas: recibe el draft como PromotionWithTargets y devuelve
   * los conflictos contra las promos existentes. Si no se pasa, no hay aviso.
   */
  onCheckDraftConflicts?: (draft: PromotionWithTargets) => PromotionConflict[];
};

export function PromotionFormModal({
  visible,
  promotionId,
  duplicateFromId = null,
  createTemplateId = null,
  onClose,
  onCheckDraftConflicts,
}: Props) {
  const vm = usePromotionEditScreen({ promotionId, duplicateFromId, createTemplateId, onClose });
  const insets = useSafeAreaInsets();
  const { height: winH } = useWindowDimensions();
  const { layoutWebShell } = useContentLayout('settings');
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [acotarCatalog, setAcotarCatalog] = useState(false);
  const [pickBranches, setPickBranches] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const seededRef = useRef(false);

  const formTitle = vm.screenTitle;
  const heroKicker = vm.isEdit ? 'Edición' : vm.isDuplicateDraft ? 'Copia' : 'Nueva';

  useEffect(() => {
    if (!visible) {
      seededRef.current = false;
      return;
    }
    if (vm.loading || seededRef.current) return;
    seededRef.current = true;
    setAcotarCatalog(vm.targets.length > 0);
    setPickBranches(vm.selectedBranchIds.length > 0);
    setAdvancedOpen(false);
    setProductSearch('');
  }, [visible, vm.loading, vm.targets.length, vm.selectedBranchIds.length]);

  useEffect(() => {
    if (vm.targets.length > 0) setAcotarCatalog(true);
  }, [vm.targets.length]);

  // ── Cambios sin guardar: baseline = snapshot de los campos apenas el form ──
  // está listo (post-load / post-seed de template). Mismo patrón que
  // AddStockModal / CatalogProductFormModal.
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [baselineKey, setBaselineKey] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Snapshot solo con ids (targets/branches ordenados): las re-resoluciones de
  // label post-load no cuentan como dirty.
  const formSnapshot = () =>
    JSON.stringify({
      name: vm.name,
      description: vm.description,
      kind: vm.kind,
      scope: vm.scope,
      valuePercent: vm.valuePercent,
      valueFixed: vm.valueFixed,
      comboBuyQty: vm.comboBuyQty,
      comboPayQty: vm.comboPayQty,
      comboBundlePrice: vm.comboBundlePrice,
      startsAt: vm.startsAt,
      endsAt: vm.endsAt,
      isActive: vm.isActive,
      applyMode: vm.applyMode,
      promoCode: vm.promoCode,
      priority: vm.priority,
      stackable: vm.stackable,
      channelPos: vm.channelPos,
      channelOnline: vm.channelOnline,
      targets: vm.targets.map((t) => `${t.targetType}:${t.targetId}`).sort(),
      selectedBranchIds: [...vm.selectedBranchIds].sort(),
      minPurchaseAmount: vm.minPurchaseAmount,
      minQuantity: vm.minQuantity,
      usageLimit: vm.usageLimit,
      selectedWeekdays: [...vm.selectedWeekdays].sort(),
      scheduleStartTime: vm.scheduleStartTime,
      scheduleEndTime: vm.scheduleEndTime,
    });

  const formReady = visible && !vm.loading && !vm.loadError;
  // Ajuste de estado en render: el primer render con el form listo congela el
  // baseline. vm.load() recién baja loading cuando terminó de seedear campos,
  // targets y templates — acá ya están todos los valores, sin dirty falso.
  if (formReady && baselineKey === null) {
    setBaselineKey(formSnapshot());
  }
  // Si el form deja de estar listo (se cerró, falló la carga), resetear.
  if (!formReady && (baselineKey !== null || discardConfirmOpen)) {
    setBaselineKey(null);
    setDiscardConfirmOpen(false);
  }

  const isDirty = baselineKey !== null && formSnapshot() !== baselineKey;
  const closeLocked = vm.saving;
  const guardedClose = useCallback(() => {
    if (closeLocked) return;
    // Si el confirm ya está arriba, Escape/back lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: 'Descartar cambios',
      message: 'Hay cambios sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  }, [closeLocked, discardConfirmOpen, isDirty, onClose, confirmDiscard]);

  // ── Pisadas con otras promos: aviso dentro del form (el detector corre con ──
  // liveOnly:false — una programada que va a pisar también avisa).
  const draftConflicts = useMemo(() => {
    if (!onCheckDraftConflicts || !vm.canEdit || vm.loading || vm.loadError) return [];
    const draftId = vm.promotionId ?? 'draft-new';
    const channels: PromotionChannel[] = [];
    if (vm.channelPos) channels.push('pos');
    if (vm.channelOnline) channels.push('online');
    const draft: PromotionWithTargets = {
      id: draftId,
      organizationId: '',
      name: vm.name,
      description: vm.description,
      kind: vm.kind,
      scope: vm.scope,
      startsAt: vm.startsAt || null,
      endsAt: vm.endsAt || null,
      isActive: vm.isActive,
      applyMode: vm.applyMode,
      promoCode: vm.promoCode || null,
      priority: 0,
      stackable: vm.stackable,
      branchIds: vm.selectedBranchIds.length > 0 ? vm.selectedBranchIds : null,
      channels,
      createdAt: '',
      updatedAt: '',
      targets: vm.targets.map((t, i) => ({
        id: t.id ?? `draft-t-${i}`,
        promotionId: draftId,
        targetType: t.targetType,
        targetId: t.targetId,
      })),
    };
    return onCheckDraftConflicts(draft);
  }, [
    onCheckDraftConflicts,
    vm.canEdit,
    vm.loading,
    vm.loadError,
    vm.promotionId,
    vm.name,
    vm.description,
    vm.kind,
    vm.scope,
    vm.startsAt,
    vm.endsAt,
    vm.isActive,
    vm.applyMode,
    vm.promoCode,
    vm.stackable,
    vm.selectedBranchIds,
    vm.channelPos,
    vm.channelOnline,
    vm.targets,
  ]);

  const draftConflictNames = useMemo(() => {
    const draftId = vm.promotionId ?? 'draft-new';
    const names = draftConflicts.map((c) =>
      c.promotionA.id === draftId ? c.promotionB.name : c.promotionA.name
    );
    return [...new Set(names)];
  }, [draftConflicts, vm.promotionId]);

  const scopeChipValue: PromotionScope = vm.scope === 'category' ? 'line' : vm.scope;
  const isLegacyCategoryScope = vm.scope === 'category';

  const previewBenefit = formatPromotionDraftBenefit({
    kind: vm.kind,
    valuePercent: vm.valuePercent,
    valueFixed: vm.valueFixed,
    comboBuyQty: vm.comboBuyQty,
    comboPayQty: vm.comboPayQty,
    comboBundlePrice: vm.comboBundlePrice,
  });

  const previewTitle = vm.name.trim() || PROMOTION_KIND_LABELS[vm.kind];

  const previewApply = useMemo(() => {
    if (vm.applyMode === 'manual_code') {
      const code = vm.promoCode.trim().toUpperCase();
      return code ? `Código ${code}` : PROMOTION_APPLY_MODE_LABELS.manual_code;
    }
    return PROMOTION_APPLY_MODE_LABELS[vm.applyMode];
  }, [vm.applyMode, vm.promoCode]);

  const previewCatalog =
    vm.targets.length === 0
      ? 'Todo el catálogo'
      : vm.targets.length === 1
        ? vm.targets[0].label
        : `${vm.targets.length} filtros`;

  const previewBranches =
    vm.selectedBranchIds.length === 0
      ? 'Todos los locales'
      : vm.selectedBranchIds.length === 1
        ? (vm.branches.find((b) => b.id === vm.selectedBranchIds[0])?.name ?? '1 local')
        : `${vm.selectedBranchIds.length} locales`;

  const previewChannels = (() => {
    if (vm.channelPos && vm.channelOnline) return 'POS + Online';
    if (vm.channelPos) return 'POS';
    if (vm.channelOnline) return 'Online';
    return 'Sin canal';
  })();

  const cashierOnlineConflict = vm.applyMode === 'cashier_select' && vm.channelOnline;

  const handleApplyModeChange = (mode: PromotionApplyMode) => {
    vm.setApplyMode(mode);
    if (mode === 'cashier_select') {
      // cashier_select solo funciona en POS: forzar POS on, online off.
      if (!vm.channelPos) vm.toggleChannel('pos', true);
      if (vm.channelOnline) vm.toggleChannel('online', false);
    }
  };

  const handleCatalogAll = () => {
    setAcotarCatalog(false);
    vm.clearTargets();
  };

  const handleBranchesAll = () => {
    setPickBranches(false);
    vm.clearSelectedBranches();
  };

  const ticketPreview = (
    <View style={localStyles.ticket} accessibilityLabel="Vista previa de la promoción">
      <Text style={localStyles.ticketKicker}>Vista previa</Text>
      <Text style={localStyles.ticketLine} numberOfLines={2}>
        {previewBenefit} · {previewTitle}
      </Text>
      <Text style={localStyles.ticketMeta} numberOfLines={3}>
        {[previewApply, previewCatalog, previewBranches, previewChannels, vm.isActive ? 'Activa' : 'Inactiva'].join(
          ' · '
        )}
      </Text>
    </View>
  );

  const valueFields = (
    <View style={localStyles.fieldGroup}>
      {vm.kind === 'percent_off' ? (
        <View>
          <Text style={localStyles.fieldLabel}>Porcentaje</Text>
          <NumericTextInput
            kind="percent"
            style={localStyles.fieldInput}
            value={vm.valuePercent}
            onChangeText={vm.setValuePercent}
            placeholder="Ej. 15"
            placeholderTextColor={ticketColors.ink2}
            editable={vm.canEdit && !vm.saving}
          />
        </View>
      ) : null}
      {vm.kind === 'fixed_off' ? (
        <View>
          <Text style={localStyles.fieldLabel}>Monto fijo ($)</Text>
          <NumericTextInput
            kind="decimalMoney"
            style={localStyles.fieldInput}
            value={vm.valueFixed}
            onChangeText={vm.setValueFixed}
            placeholder="Ej. 500"
            placeholderTextColor={ticketColors.ink2}
            editable={vm.canEdit && !vm.saving}
          />
        </View>
      ) : null}
      {vm.kind === 'combo_buy_x_get_y' || vm.kind === 'combo_n_for_price' ? (
        <View style={localStyles.twoCol}>
          <View style={{ flex: 1 }}>
            <Text style={localStyles.fieldLabel}>Comprás</Text>
            <NumericTextInput
              kind="integer"
              style={localStyles.fieldInput}
              value={vm.comboBuyQty}
              onChangeText={vm.setComboBuyQty}
              placeholder="Ej. 3"
              placeholderTextColor={ticketColors.ink2}
              editable={vm.canEdit && !vm.saving}
            />
          </View>
          {vm.kind === 'combo_buy_x_get_y' ? (
            <View style={{ flex: 1 }}>
              <Text style={localStyles.fieldLabel}>Pagás</Text>
              <NumericTextInput
                kind="integer"
                style={localStyles.fieldInput}
                value={vm.comboPayQty}
                onChangeText={vm.setComboPayQty}
                placeholder="Ej. 2"
                placeholderTextColor={ticketColors.ink2}
                editable={vm.canEdit && !vm.saving}
              />
            </View>
          ) : (
            <View style={{ flex: 1 }}>
              <Text style={localStyles.fieldLabel}>Precio del pack ($)</Text>
              <NumericTextInput
                kind="decimalMoney"
                style={localStyles.fieldInput}
                value={vm.comboBundlePrice}
                onChangeText={vm.setComboBundlePrice}
                placeholder="Ej. 1500"
                placeholderTextColor={ticketColors.ink2}
                editable={vm.canEdit && !vm.saving}
              />
            </View>
          )}
        </View>
      ) : null}
      {vm.comboBundleWarning ? (
        <View style={modalExperience.warningPanel}>
          <Text style={localStyles.helperText}>{vm.comboBundleWarning}</Text>
        </View>
      ) : null}
    </View>
  );

  const formFields = (
    <>
      {!vm.canEdit ? (
        <View style={[posUi.readOnlyBanner, localStyles.readonlyBanner]}>
          <Text style={posUi.readOnlyText}>
            Solo lectura. Podés mirar la promo, no guardar.
          </Text>
        </View>
      ) : null}

      <View style={modalExperience.formPanel}>
        <ModalFormSection title="1. Qué oferta" helper="Elegí el tipo. El resto se arma solo.">
          <View style={localStyles.kindGrid}>
            {KIND_OPTIONS.map((opt) => {
              const active = vm.kind === opt.value;
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[localStyles.kindCard, active && localStyles.kindCardActive]}
                  onPress={() => vm.setKind(opt.value)}
                  disabled={!vm.canEdit || vm.saving}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: active, disabled: !vm.canEdit || vm.saving }}
                >
                  <Text style={[localStyles.kindTitle, active && localStyles.kindTitleActive]}>{opt.label}</Text>
                  {opt.hint ? <Text style={localStyles.kindHint}>{opt.hint}</Text> : null}
                </TouchableOpacity>
              );
            })}
          </View>
        </ModalFormSection>

        <ModalFormSection
          title="2. Cómo se activa"
          divided
          helper="Cómo se aplica en el POS o online."
        >
          <View style={localStyles.modeList}>
            {APPLY_MODE_OPTIONS.map((opt) => {
              const active = vm.applyMode === opt.value;
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[localStyles.modeCard, active && localStyles.modeCardActive]}
                  onPress={() => handleApplyModeChange(opt.value)}
                  disabled={!vm.canEdit || vm.saving}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: active }}
                >
                  <Text style={[localStyles.modeTitle, active && localStyles.modeTitleActive]}>{opt.label}</Text>
                  <Text style={localStyles.modeHelper}>{opt.helper}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {vm.applyMode === 'manual_code' ? (
            <View>
              <Text style={localStyles.fieldLabel}>Código</Text>
              <TextInput
                style={localStyles.fieldInput}
                value={vm.promoCode}
                onChangeText={vm.setPromoCode}
                placeholder="Ej. VERANO20"
                placeholderTextColor={ticketColors.ink2}
                autoCapitalize="characters"
                editable={vm.canEdit && !vm.saving}
              />
            </View>
          ) : null}
          {cashierOnlineConflict ? (
            <View style={modalExperience.warningPanel}>
              <Text style={localStyles.helperText}>
                Solo mostrador. Si el cajero la elige, no vale online.
              </Text>
            </View>
          ) : null}
        </ModalFormSection>

        <ModalFormSection title="3. Cuánto y nombre" divided>
          {valueFields}
          <View>
            <Text style={localStyles.fieldLabel}>Nombre</Text>
            <TextInput
              style={localStyles.fieldInput}
              value={vm.name}
              onChangeText={vm.setName}
              placeholder="Ej. 2x1 gaseosas"
              placeholderTextColor={ticketColors.ink2}
              editable={vm.canEdit && !vm.saving}
            />
          </View>
        </ModalFormSection>

        <ModalFormSection title="4. A qué productos" divided helper={PROMOTION_APPLIES_TO_HELPER}>
          <View style={localStyles.chipRow}>
            <TouchableOpacity
              style={[localStyles.segChip, !acotarCatalog && localStyles.segChipActive]}
              onPress={handleCatalogAll}
              disabled={!vm.canEdit || vm.saving}
              accessibilityRole="button"
              accessibilityState={{ selected: !acotarCatalog }}
            >
              <Text style={[localStyles.segChipText, !acotarCatalog && localStyles.segChipTextActive]}>
                Todo el catálogo
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[localStyles.segChip, acotarCatalog && localStyles.segChipActive]}
              onPress={() => setAcotarCatalog(true)}
              disabled={!vm.canEdit || vm.saving}
              accessibilityRole="button"
              accessibilityState={{ selected: acotarCatalog }}
            >
              <Text style={[localStyles.segChipText, acotarCatalog && localStyles.segChipTextActive]}>
                Elegir productos
              </Text>
            </TouchableOpacity>
          </View>

          {acotarCatalog ? (
            <>
              {vm.targets.length === 0 ? (
                <Text style={localStyles.helperText}>Agregá al menos un filtro, o volvé a «Todo el catálogo».</Text>
              ) : (
                <View style={localStyles.targetList}>
                  {vm.targets.map((target) => (
                    <View key={`${target.targetType}:${target.targetId}`} style={localStyles.targetChip}>
                      <Text style={localStyles.targetChipText} numberOfLines={2}>
                        {PROMOTION_TARGET_TYPE_LABELS[target.targetType]}: {target.label}
                      </Text>
                      {vm.canEdit ? (
                        <TouchableOpacity
                          onPress={() => vm.removeTarget(target.targetType, target.targetId)}
                          accessibilityRole="button"
                          accessibilityLabel="Quitar filtro"
                        >
                          <Ionicons name="close-circle" size={18} color={ticketColors.ink2} />
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  ))}
                </View>
              )}
              {vm.canEdit ? (
                <View style={localStyles.targetActions}>
                  <TouchableOpacity
                    style={localStyles.secondaryBtn}
                    onPress={() => setCategoryPickerOpen(true)}
                    disabled={vm.saving || vm.mastersLoading}
                  >
                    <Text style={localStyles.secondaryBtnText}>+ Categoría</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={localStyles.secondaryBtn}
                    onPress={() => setBrandPickerOpen(true)}
                    disabled={vm.saving || vm.mastersLoading}
                  >
                    <Text style={localStyles.secondaryBtnText}>+ Marca</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={localStyles.secondaryBtn}
                    onPress={() => setVariantPickerOpen(true)}
                    disabled={vm.saving || vm.mastersLoading}
                  >
                    <Text style={localStyles.secondaryBtnText}>+ Variante</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
              {vm.canEdit ? (
                <View style={localStyles.productSearchBlock}>
                  <Text style={localStyles.fieldLabel}>Agregar producto</Text>
                  <CatalogStockProductSearchFields
                    value={productSearch}
                    onChangeText={setProductSearch}
                    branchId={vm.currentBranchId}
                    searchProductsFn={searchProducts}
                    placeholder="Buscar por nombre o código…"
                    onPickSuggestion={(row) => {
                      vm.addTarget({
                        targetType: 'product',
                        targetId: row.id,
                        label: row.name,
                      });
                      setProductSearch('');
                    }}
                    sectionLabel={null}
                  />
                </View>
              ) : null}
            </>
          ) : null}
        </ModalFormSection>

        <ModalFormSection title="5. Dónde vale" divided>
          {vm.branches.length > 0 ? (
            <View style={localStyles.fieldGroup}>
              <Text style={localStyles.fieldLabel}>Locales</Text>
              <View style={localStyles.chipRow}>
                <TouchableOpacity
                  style={[localStyles.segChip, !pickBranches && localStyles.segChipActive]}
                  onPress={handleBranchesAll}
                  disabled={!vm.canEdit || vm.saving}
                  accessibilityRole="button"
                  accessibilityState={{ selected: !pickBranches }}
                >
                  <Text style={[localStyles.segChipText, !pickBranches && localStyles.segChipTextActive]}>
                    Todas
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[localStyles.segChip, pickBranches && localStyles.segChipActive]}
                  onPress={() => setPickBranches(true)}
                  disabled={!vm.canEdit || vm.saving}
                  accessibilityRole="button"
                  accessibilityState={{ selected: pickBranches }}
                >
                  <Text style={[localStyles.segChipText, pickBranches && localStyles.segChipTextActive]}>
                    Elegir…
                  </Text>
                </TouchableOpacity>
              </View>
              {pickBranches ? (
                <View style={localStyles.branchList}>
                  {vm.branches.map((branch) => {
                    const checked = vm.selectedBranchIds.includes(branch.id);
                    return (
                      <TouchableOpacity
                        key={branch.id}
                        style={localStyles.branchRow}
                        onPress={() => vm.toggleBranchId(branch.id)}
                        disabled={!vm.canEdit || vm.saving}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked }}
                      >
                        <Ionicons
                          name={checked ? 'checkbox' : 'square-outline'}
                          size={22}
                          color={checked ? ticketColors.gold : ticketColors.ink2}
                        />
                        <Text style={localStyles.branchLabel}>{branch.name ?? branch.id}</Text>
                      </TouchableOpacity>
                    );
                  })}
                  {vm.selectedBranchIds.length === 0 ? (
                    <Text style={localStyles.helperText}>
                      Sin selección vale en todos los locales. Marcá uno para acotar.
                    </Text>
                  ) : null}
                </View>
              ) : null}
            </View>
          ) : null}

          <View>
            <Text style={localStyles.fieldLabel}>Canales</Text>
            <View style={localStyles.chipRow}>
              <TouchableOpacity
                style={[localStyles.segChip, vm.channelPos && localStyles.segChipActive]}
                onPress={() => {
                  if (vm.channelPos && !vm.channelOnline) {
                    showInfo('Canal requerido', 'Al menos un canal tiene que estar activo.');
                    return;
                  }
                  if (vm.channelPos && vm.applyMode === 'cashier_select') {
                    showInfo('Canal requerido', 'POS es obligatorio cuando la elige el cajero.');
                    return;
                  }
                  vm.toggleChannel('pos', !vm.channelPos);
                }}
                disabled={!vm.canEdit || vm.saving}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: vm.channelPos }}
              >
                <Text style={[localStyles.segChipText, vm.channelPos && localStyles.segChipTextActive]}>POS</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[localStyles.segChip, vm.channelOnline && localStyles.segChipActive]}
                onPress={() => {
                  if (vm.channelOnline && !vm.channelPos) {
                    showInfo('Canal requerido', 'Al menos un canal tiene que estar activo.');
                    return;
                  }
                  vm.toggleChannel('online', !vm.channelOnline);
                }}
                disabled={!vm.canEdit || vm.saving}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: vm.channelOnline }}
              >
                <Text style={[localStyles.segChipText, vm.channelOnline && localStyles.segChipTextActive]}>
                  Online
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ModalFormSection>

        <ModalFormSection title="6. Cuándo" divided>
          <View style={localStyles.dateRow}>
            <View style={localStyles.dateCol}>
              <Text style={localStyles.fieldLabel}>Desde</Text>
              <InventoryIsoDateField
                value={vm.startsAt}
                onChange={vm.setStartsAt}
                emptyLabel="Sin inicio"
                ariaLabel="Fecha inicio promoción"
                clearButtonLabel="Quitar"
                iosEmptyBehavior="openPicker"
              />
            </View>
            <View style={localStyles.dateCol}>
              <Text style={localStyles.fieldLabel}>Hasta</Text>
              <InventoryIsoDateField
                value={vm.endsAt}
                onChange={vm.setEndsAt}
                emptyLabel="Sin fin"
                ariaLabel="Fecha fin promoción"
                clearButtonLabel="Quitar"
                iosEmptyBehavior="openPicker"
              />
            </View>
          </View>
          <View style={localStyles.switchRow}>
            <Text style={localStyles.fieldLabel}>Activa</Text>
            <Switch
              value={vm.isActive}
              onValueChange={vm.setIsActive}
              disabled={!vm.canEdit || vm.saving}
            />
          </View>
        </ModalFormSection>
      </View>

      {draftConflictNames.length > 0 ? (
        <View style={modalExperience.warningPanel} testID="promociones.conflict-warning">
          <Text style={localStyles.helperText}>
            Se pisa con {draftConflictNames.join(', ')}. No se suman: en caja entra una sola.
          </Text>
        </View>
      ) : null}

      <View style={localStyles.advanced}>
        <TouchableOpacity
          style={localStyles.advancedHeader}
          onPress={() => setAdvancedOpen((v) => !v)}
          accessibilityRole="button"
          accessibilityState={{ expanded: advancedOpen }}
          accessibilityLabel={`Más opciones, ${advancedOpen ? 'contraer' : 'expandir'}`}
        >
          <Text style={localStyles.advancedTitle}>Más opciones</Text>
          <Ionicons
            name={advancedOpen ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={ticketColors.ink2}
          />
        </TouchableOpacity>
        {advancedOpen ? (
          <View style={localStyles.advancedBody}>
            <View>
              <Text style={localStyles.fieldLabel}>Cómo se cuenta</Text>
              <Text style={localStyles.helperText}>
                No elige productos. Eso es «A qué productos». Acá definís si cuenta por producto o por
                carrito.
              </Text>
              <View style={localStyles.chipBlock}>
                <OperationalFilterChipGroup
                  options={SCOPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                    accessibilityLabel: opt.label,
                  }))}
                  value={scopeChipValue}
                  disabled={!vm.canEdit || vm.saving}
                  onChange={(value) => vm.setScope(value as PromotionScope)}
                />
              </View>
              <Text style={localStyles.helperText}>{getPromotionScopeHelperText(scopeChipValue, vm.kind)}</Text>
              {isLegacyCategoryScope ? (
                <Text style={[localStyles.helperText, { color: ticketColors.gold }]}>
                  Esta promo usaba scope «categoría» (legacy). Se migrará a «por producto» al guardar.
                </Text>
              ) : null}
            </View>

            <View style={localStyles.twoCol}>
              <View style={{ flex: 1 }}>
                <Text style={localStyles.fieldLabel}>Compra mínima ($)</Text>
                <NumericTextInput
                  kind="decimalMoney"
                  style={localStyles.fieldInput}
                  value={vm.minPurchaseAmount}
                  onChangeText={vm.setMinPurchaseAmount}
                  placeholder="Sin mínimo"
                  placeholderTextColor={ticketColors.ink2}
                  editable={vm.canEdit && !vm.saving}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={localStyles.fieldLabel}>Unidades mínimas</Text>
                <NumericTextInput
                  kind="integer"
                  style={localStyles.fieldInput}
                  value={vm.minQuantity}
                  onChangeText={vm.setMinQuantity}
                  placeholder="Sin mínimo"
                  placeholderTextColor={ticketColors.ink2}
                  editable={vm.canEdit && !vm.saving}
                />
              </View>
            </View>

            <View>
              <Text style={localStyles.fieldLabel}>Límite total de usos</Text>
              <NumericTextInput
                kind="integer"
                style={localStyles.fieldInput}
                value={vm.usageLimit}
                onChangeText={vm.setUsageLimit}
                placeholder="Ilimitado"
                placeholderTextColor={ticketColors.ink2}
                editable={vm.canEdit && !vm.saving}
              />
              <Text style={localStyles.helperText}>
                El POS cuenta los usos. Cuando llega al límite, la promo deja de aplicar.
              </Text>
            </View>

            <View>
              <Text style={localStyles.fieldLabel}>Días de la semana</Text>
              <View style={localStyles.weekdayRow}>
                {WEEKDAY_OPTIONS.map((day) => {
                  const checked = vm.selectedWeekdays.includes(day.value);
                  return (
                    <TouchableOpacity
                      key={day.value}
                      style={[localStyles.weekdayChip, checked && localStyles.weekdayChipActive]}
                      onPress={() => vm.toggleWeekday(day.value)}
                      disabled={!vm.canEdit || vm.saving}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked }}
                    >
                      <Text
                        style={[localStyles.weekdayChipText, checked && localStyles.weekdayChipTextActive]}
                      >
                        {day.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            <View>
              <Text style={localStyles.fieldLabel}>Horario (opcional)</Text>
              <View style={localStyles.twoCol}>
                <View style={{ flex: 1 }}>
                  <Text style={localStyles.fieldLabel}>Desde</Text>
                  <TextInput
                    style={localStyles.fieldInput}
                    value={vm.scheduleStartTime}
                    onChangeText={vm.setScheduleStartTime}
                    placeholder="HH:mm"
                    placeholderTextColor={ticketColors.ink2}
                    editable={vm.canEdit && !vm.saving}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={localStyles.fieldLabel}>Hasta</Text>
                  <TextInput
                    style={localStyles.fieldInput}
                    value={vm.scheduleEndTime}
                    onChangeText={vm.setScheduleEndTime}
                    placeholder="HH:mm"
                    placeholderTextColor={ticketColors.ink2}
                    editable={vm.canEdit && !vm.saving}
                  />
                </View>
              </View>
            </View>

            <View>
              <Text style={localStyles.fieldLabel}>Prioridad</Text>
              <NumericTextInput
                kind="integer"
                style={localStyles.fieldInput}
                value={vm.priority}
                onChangeText={vm.setPriority}
                placeholder="0"
                placeholderTextColor={ticketColors.ink2}
                editable={vm.canEdit && !vm.saving}
              />
              <Text style={localStyles.helperText}>Cuanto más alto, más primero se aplica entre las automáticas.</Text>
            </View>

            <View style={localStyles.switchRow}>
              <Text style={localStyles.fieldLabel}>Se suma a otras promos</Text>
              <Switch
                value={vm.stackable}
                onValueChange={vm.setStackable}
                disabled={!vm.canEdit || vm.saving}
              />
            </View>

            <View>
              <Text style={localStyles.fieldLabel}>Descripción interna</Text>
              <TextInput
                style={[localStyles.fieldInput, localStyles.multiline]}
                value={vm.description}
                onChangeText={vm.setDescription}
                placeholder="Notas para el equipo"
                placeholderTextColor={ticketColors.ink2}
                multiline
                editable={vm.canEdit && !vm.saving}
              />
            </View>
          </View>
        ) : null}
      </View>

      {vm.validationMessage ? (
        <View style={modalExperience.warningPanel}>
          <Text style={localStyles.validationText}>{vm.validationMessage}</Text>
        </View>
      ) : null}
    </>
  );

  const nestedPickers = (
    <>
      <CatalogCategorySelectorModal
        visible={categoryPickerOpen}
        title="Elegir categoría"
        subtitle="La promo vale solo para productos de esta categoría."
        options={vm.categoryOptions}
        value=""
        loading={vm.mastersLoading}
        onClose={() => setCategoryPickerOpen(false)}
        onSelect={(categoryId) => {
          const label = vm.categoryOptions.find((c) => c.id === categoryId)?.label ?? categoryId;
          vm.addTarget({ targetType: 'category', targetId: categoryId, label });
          setCategoryPickerOpen(false);
          setAcotarCatalog(true);
        }}
      />

      <ModalNestedPicker
        visible={brandPickerOpen}
        title="Elegir marca"
        options={vm.brandPickerOptions}
        selectedValue=""
        onSelect={(brandId) => {
          const label = vm.brandPickerOptions.find((b) => b.value === brandId)?.label ?? brandId;
          vm.addTarget({ targetType: 'brand', targetId: brandId, label });
          setBrandPickerOpen(false);
          setAcotarCatalog(true);
        }}
        onClose={() => setBrandPickerOpen(false)}
      />

      <PromotionVariantPickerModal
        visible={variantPickerOpen}
        onClose={() => setVariantPickerOpen(false)}
        onPick={(picks) => {
          // #714: onPick ahora recibe múltiples variantes a la vez.
          for (const pick of picks) {
            vm.addTarget({ targetType: 'variant', targetId: pick.variantId, label: pick.label });
          }
          setVariantPickerOpen(false);
          setAcotarCatalog(true);
        }}
      />
    </>
  );

  const footerActions = (
    <View style={localStyles.footerRow}>
      <TouchableOpacity
        style={[localStyles.cancelBtn, { flex: 1 }]}
        onPress={guardedClose}
        disabled={vm.saving}
        accessibilityRole="button"
        accessibilityLabel="Cancelar"
      >
        <Text style={localStyles.cancelBtnText}>Cancelar</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[localStyles.saveBtn, { flex: 1 }, vm.saving && localStyles.saveBtnDisabled]}
        onPress={() => void vm.onSave()}
        disabled={vm.saving}
        accessibilityRole="button"
        testID="promociones.save"
      >
        <Text style={localStyles.saveBtnText}>{vm.saving ? 'Guardando…' : 'Guardar'}</Text>
      </TouchableOpacity>
    </View>
  );

  const readOnlyFooter = (
    <View style={localStyles.footerRow}>
      <TouchableOpacity
        style={[localStyles.cancelBtn, { flex: 1 }]}
        onPress={guardedClose}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      >
        <Text style={localStyles.cancelBtnText}>Cerrar</Text>
      </TouchableOpacity>
    </View>
  );

  if (vm.loading) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <Pressable style={modalStyles.backdrop} onPress={onClose}>
          <View style={modalStyles.loadingCard}>
            <Text style={modalStyles.loadingText}>Cargando promoción…</Text>
          </View>
        </Pressable>
      </Modal>
    );
  }

  if (vm.loadError) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <Pressable style={modalStyles.backdrop} onPress={onClose}>
          <View style={modalStyles.loadingCard}>
            <Text style={modalStyles.loadingText}>{vm.loadError}</Text>
            <TouchableOpacity
              style={modalStyles.errorCloseBtn}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={modalStyles.errorCloseText}>Cerrar</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    );
  }

  if (layoutWebShell) {
    return (
      <>
        <Modal visible={visible} transparent animationType="fade" onRequestClose={vm.saving ? undefined : guardedClose}>
          <Pressable style={modalStyles.backdrop} onPress={vm.saving ? undefined : guardedClose}>
            <Pressable style={modalStyles.wideCard} onPress={(e) => e.stopPropagation()}>
              <View style={modalStyles.wideHeader}>
                <View style={modalStyles.wideHeaderTitleBlock}>
                  <Text style={modalStyles.wideKicker}>{heroKicker}</Text>
                  <Text style={modalStyles.wideTitle}>{formTitle}</Text>
                </View>
                <TouchableOpacity
                  onPress={guardedClose}
                  style={modalStyles.closeBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Ionicons name="close" size={18} color={ticketColors.ink3} />
                </TouchableOpacity>
              </View>
              <View style={modalStyles.previewDock}>{ticketPreview}</View>
              <ModalAwareScrollView
                ref={scrollRef}
                style={modalStyles.wideScroll}
                contentContainerStyle={modalStyles.wideScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                bottomOffset={spacing.sm}
              >
                {formFields}
              </ModalAwareScrollView>
              {vm.canEdit ? <View style={modalStyles.wideDock}>{footerActions}</View> : <View style={modalStyles.wideDock}>{readOnlyFooter}</View>}
            </Pressable>
          </Pressable>
        </Modal>
        {nestedPickers}
        {DiscardConfirmModal}
      </>
    );
  }

  return (
    <>
      <Modal visible={visible} transparent animationType="slide" onRequestClose={vm.saving ? undefined : guardedClose}>
        <View style={modalStyles.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={vm.saving ? undefined : guardedClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={modalStyles.keyboardWrap}>
            <View style={[modalStyles.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={modalStyles.grabber} />
              <View style={modalStyles.sheetHeader}>
                <Text style={modalStyles.sheetTitle}>{formTitle}</Text>
                <TouchableOpacity
                  onPress={guardedClose}
                  style={modalStyles.sheetCloseBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Ionicons name="close" size={16} color={ticketColors.ink3} />
                </TouchableOpacity>
              </View>
              <View style={modalStyles.previewDockSheet}>{ticketPreview}</View>
              <ModalAwareScrollView
                ref={scrollRef}
                style={modalStyles.sheetScroll}
                contentContainerStyle={modalStyles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                showsHorizontalScrollIndicator={false}
                bottomOffset={spacing.sm}
              >
                <View collapsable={false} style={modalExperience.formStack}>
                  {formFields}
                </View>
              </ModalAwareScrollView>
              {vm.canEdit ? <View style={modalStyles.sheetDock}>{footerActions}</View> : <View style={modalStyles.sheetDock}>{readOnlyFooter}</View>}
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
      {nestedPickers}
      {DiscardConfirmModal}
    </>
  );
}

const modalStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.md,
  },
  loadingCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: spacing.xl,
    alignItems: 'center',
  },
  loadingText: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  errorCloseBtn: {
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    backgroundColor: ticketColors.gold,
  },
  errorCloseText: {
    ...typography.caption,
    fontWeight: '700',
    color: ticketColors.paper,
  },
  wideCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    maxWidth: 640,
    width: '100%',
    maxHeight: '96%',
    overflow: 'hidden',
  },
  wideHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  wideHeaderTitleBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  wideKicker: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  wideTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  previewDock: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
    backgroundColor: ticketColors.paper,
  },
  wideScroll: {
    flexShrink: 1,
  },
  wideScrollContent: {
    padding: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
    gap: spacing.lg,
  },
  wideDock: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    maxHeight: '96%',
    width: '100%',
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginBottom: spacing.sm,
  },
  sheetTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  sheetCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  previewDockSheet: {
    paddingHorizontal: 20,
    paddingBottom: spacing.sm,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.lg,
  },
  sheetDock: {
    paddingHorizontal: 20,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
});

const localStyles = StyleSheet.create({
  ticket: {
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    padding: spacing.md,
    gap: spacing.xs,
  },
  ticketKicker: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.gold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  ticketLine: {
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink,
    lineHeight: 21,
  },
  ticketMeta: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    lineHeight: 17,
  },
  kindGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  kindCard: {
    flexGrow: 1,
    flexBasis: '46%',
    maxWidth: '48.5%',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: 14,
    paddingHorizontal: 12,
    gap: 4,
    minHeight: 64,
  },
  kindCardActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  kindTitle: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  kindTitleActive: {
    color: ticketColors.gold,
  },
  kindHint: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink2,
    lineHeight: 15,
  },
  modeList: {
    gap: spacing.sm,
  },
  modeCard: {
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 4,
  },
  modeCardActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  modeTitle: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  modeTitleActive: {
    color: ticketColors.gold,
  },
  modeHelper: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    lineHeight: 17,
  },
  fieldLabel: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  fieldInput: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    color: ticketColors.ink,
    ...typography.body,
    paddingVertical: 12,
    paddingHorizontal: 14,
    minHeight: 44,
  },
  fieldGroup: {
    gap: 12,
  },
  twoCol: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  segChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: 7,
    paddingHorizontal: 12,
  },
  segChipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  segChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  segChipTextActive: {
    color: '#FFFFFF',
  },
  chipBlock: {
    marginTop: spacing.sm,
  },
  helperText: {
    fontSize: 12,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
    lineHeight: 18,
  },
  targetList: {
    gap: spacing.sm,
  },
  targetChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  targetChipText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  targetActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  productSearchBlock: {
    gap: spacing.xs,
  },
  secondaryBtn: {
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderStyle: 'dashed',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtnText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '600',
  },
  branchList: {
    gap: spacing.sm,
  },
  branchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: ticketColors.paper,
  },
  branchLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  dateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dateCol: {
    flex: 1,
    gap: spacing.xs,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minHeight: 36,
  },
  advanced: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
  },
  advancedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  advancedTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  advancedBody: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    padding: 14,
    gap: spacing.md,
  },
  weekdayRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  weekdayChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: ticketColors.paper,
  },
  weekdayChipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  weekdayChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  weekdayChipTextActive: {
    color: '#FFFFFF',
  },
  multiline: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  readonlyBanner: {
    marginBottom: 0,
  },
  validationText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  footerRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  cancelBtn: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  cancelBtnText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    fontSize: 16,
  },
  saveBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  saveBtnText: {
    ...typography.body,
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 16,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
});
